import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * The fields we can fill from a spreadsheet. `aliases` are lower-cased header
 * fragments — most real sheets use one of these, so the mapping is usually
 * correct before the person touches anything.
 */
const FIELDS = [
  {
    key: "name",
    label: "Item name",
    required: true,
    aliases: ["item name", "product name", "product", "description", "name"],
  },
  {
    key: "category",
    label: "Category",
    aliases: ["category", "type", "group", "department"],
  },
  {
    key: "item_code",
    label: "Item code",
    aliases: ["item code", "code", "sku", "item id", "barcode"],
  },
  {
    key: "unit_cost",
    label: "Unit cost",
    aliases: ["unit cost", "cost", "buy price", "cost price", "purchase price"],
  },
  {
    key: "selling_price",
    label: "Selling price",
    aliases: ["selling price", "price", "sell", "retail", "sale price"],
  },
  {
    key: "opening_qty",
    label: "Quantity",
    aliases: ["quantity", "qty", "opening", "stock", "on hand", "count"],
  },
  {
    key: "acquisition_date",
    label: "Date acquired",
    aliases: ["date", "acquired", "purchase date", "acquisition"],
  },
];

const NONE = "__none__";

const norm = (v) => String(v).toLowerCase().trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

/**
 * Match spreadsheet columns to fields.
 *
 * Exact matches are claimed first, across every field, before any loose
 * matching happens. Without that, "Item Name" and "Item Code" fight over the
 * word "item" and whichever field is checked first wins — which is how a column
 * of codes ended up mapped to the item name.
 */
function guessMapping(headers) {
  const used = new Set();
  const mapping = Object.fromEntries(FIELDS.map((f) => [f.key, NONE]));

  // Pass one: a header that exactly equals one of a field's aliases.
  for (const field of FIELDS) {
    const hit = headers.find(
      (h) => !used.has(h) && field.aliases.some((a) => norm(h) === a)
    );
    if (hit) {
      mapping[field.key] = hit;
      used.add(hit);
    }
  }

  // Pass two: anything still unmatched, by containment. Longer aliases are
  // tried first so "item name" beats "name".
  for (const field of FIELDS) {
    if (mapping[field.key] !== NONE) continue;
    const aliases = [...field.aliases].sort((a, b) => b.length - a.length);
    const hit = headers.find(
      (h) => !used.has(h) && aliases.some((a) => norm(h).includes(a))
    );
    if (hit) {
      mapping[field.key] = hit;
      used.add(hit);
    }
  }

  return mapping;
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  // Tolerate "1,250", "$1250", "1 250"
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function toDate(value) {
  if (!value) return undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
}

export default function ImportDialog({ open, onOpenChange, onImported }) {
  const confirm = useConfirm();
  const { settings, categories, refresh } = useSettings();
  const fileRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [mode, setMode] = useState("add");

  const reset = () => {
    setFileName("");
    setHeaders([]);
    setRows([]);
    setMapping({});
    setMode("add");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      let parsedRows = [];
      let parsedHeaders = [];

      if (/\.csv$/i.test(file.name)) {
        // Loaded on demand so the parsers stay out of the main bundle.
        const { default: Papa } = await import("papaparse");
        const text = await file.text();
        const out = Papa.parse(text, { header: true, skipEmptyLines: true });
        parsedRows = out.data;
        parsedHeaders = out.meta.fields || [];
      } else {
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        parsedRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        parsedHeaders = Object.keys(parsedRows[0] || {});
      }

      if (parsedRows.length === 0) {
        toast.error("That file has no rows in its first sheet");
        return;
      }

      setFileName(file.name);
      setHeaders(parsedHeaders);
      setRows(parsedRows);
      setMapping(guessMapping(parsedHeaders));
    } catch (err) {
      toast.error("Couldn't read that file. CSV and Excel (.xlsx) are supported.");
    } finally {
      setReading(false);
    }
  };

  // Build the payload and surface anything the person should know before importing.
  const prepared = useMemo(() => {
    if (!rows.length) return null;
    const col = (row, key) => {
      const header = mapping[key];
      if (!header || header === NONE) return "";
      return row[header];
    };

    const knownCategories = new Set(categories.map((c) => c.name.toLowerCase()));
    const items = [];
    const newCategories = new Set();
    let skipped = 0;
    let missingCost = 0;
    let missingCategory = 0;

    for (const row of rows) {
      const name = String(col(row, "name") || "").trim();
      if (!name) {
        skipped += 1;
        continue;
      }
      const category = String(col(row, "category") || "").trim();
      const unitCost = toNumber(col(row, "unit_cost"));
      const sell = col(row, "selling_price");
      const code = String(col(row, "item_code") || "").trim();

      if (!unitCost) missingCost += 1;
      if (!category) missingCategory += 1;
      else if (!knownCategories.has(category.toLowerCase())) newCategories.add(category);

      items.push({
        item_code: code || undefined,
        name,
        category: category || "Uncategorised",
        unit_cost: unitCost,
        selling_price:
          sell === "" || sell === undefined
            ? undefined
            : toNumber(sell),
        opening_qty: Math.round(toNumber(col(row, "opening_qty"))),
        acquisition_date: toDate(col(row, "acquisition_date")),
      });
    }

    return { items, newCategories: [...newCategories], skipped, missingCost, missingCategory };
  }, [rows, mapping, categories]);

  const nameMapped = mapping.name && mapping.name !== NONE;
  const codeMapped = mapping.item_code && mapping.item_code !== NONE;

  const runImport = async () => {
    if (!prepared?.items.length) return;
    if (mode === "replace") {
      const ok = await confirm({
        title: "Replace everything first?",
        body: "All existing items, purchases, sales and gift boxes for this business will be deleted before the file is loaded.",
        confirmText: "Replace everything",
        destructive: true,
      });
      if (!ok) return;
    }

    setBusy(true);
    try {
      // Any category named in the sheet but not yet set up is created first,
      // otherwise those items would land as Uncategorised.
      for (const name of prepared.newCategories) {
        const letters = name.replace(/[^A-Za-z]/g, "").toUpperCase();
        const prefix = (letters.slice(0, 2) || "XX").padEnd(2, "X");
        try {
          await api.post("/categories", { prefix, name });
        } catch {
          // A clashing prefix isn't fatal — the item keeps the category name.
        }
      }

      const { data } = await api.post(
        "/admin/import",
        {
          categories: [],
          items: prepared.items,
          purchases: [],
          sales: [],
          gift_boxes: [],
        },
        {
          params: {
            replace: mode === "replace",
            mode: mode === "update" ? "update" : "add",
          },
        }
      );

      const { items: added, updated } = data.imported;
      toast.success(
        updated
          ? `${added} added, ${updated} updated`
          : `Imported ${added} item${added === 1 ? "" : "s"}`
      );
      await refresh();
      onImported?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import a spreadsheet</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file of items. Everything imports into{" "}
            {settings.business_name}.
          </DialogDescription>
        </DialogHeader>

        {!rows.length ? (
          <div className="py-2">
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-2xl border-2 border-dashed border-line hover:border-brand-ring hover:bg-brand-light/30 transition-colors py-12 flex flex-col items-center gap-3"
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center">
                {reading ? (
                  <Loader2 className="w-5 h-5 text-brand animate-spin" />
                ) : (
                  <Upload className="w-5 h-5 text-brand" />
                )}
              </div>
              <div className="text-[14px] font-medium text-ink">
                {reading ? "Reading your file…" : "Choose a file"}
              </div>
              <div className="text-[12.5px] text-muted2">CSV or Excel (.xlsx, .xls)</div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <div className="mt-5 rounded-xl bg-surface border border-line p-4">
              <div className="text-[13px] font-medium text-ink">What it looks for</div>
              <p className="text-[12.5px] text-muted2 mt-1.5 leading-relaxed">
                A header row, then one item per row. Columns named like Item Name,
                Category, Unit Cost, Selling Price and Quantity are matched
                automatically — you can correct any of them before importing. Only
                the item name is required.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-1">
            <div className="flex items-center gap-3 rounded-xl border border-line px-3.5 py-3">
              <FileSpreadsheet className="w-4 h-4 text-brand shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium text-ink truncate">{fileName}</div>
                <div className="text-[11.5px] text-muted2">
                  {rows.length} row{rows.length === 1 ? "" : "s"} · {headers.length} columns
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={reset} className="shrink-0">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div>
              <Label className="mb-2 block">Match your columns</Label>
              <div className="space-y-2">
                {FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center gap-3">
                    <div className="w-[120px] shrink-0 text-[13px] text-ink">
                      {f.label}
                      {f.required && <span className="text-stock-out ml-0.5">*</span>}
                    </div>
                    <Select
                      value={mapping[f.key] || NONE}
                      onValueChange={(v) => setMapping({ ...mapping, [f.key]: v })}
                    >
                      <SelectTrigger className="flex-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>
                          <span className="text-muted2">Not in my file</span>
                        </SelectItem>
                        {headers.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            {prepared && (
              <div className="rounded-xl border border-line overflow-hidden">
                <div className="px-4 py-2.5 border-b border-line bg-surface/60">
                  <div className="eyebrow">First few rows</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="t-head px-3 py-2 text-left">Name</th>
                        <th className="t-head px-3 py-2 text-left">Category</th>
                        <th className="t-head px-3 py-2 text-right">Cost</th>
                        <th className="t-head px-3 py-2 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prepared.items.slice(0, 4).map((i, idx) => (
                        <tr key={idx} className="border-b border-line/60 last:border-0">
                          <td className="px-3 py-2 text-ink truncate max-w-[200px]">{i.name}</td>
                          <td className="px-3 py-2 text-muted2">{i.category}</td>
                          <td className="px-3 py-2 text-right tnum">{i.unit_cost || "—"}</td>
                          <td className="px-3 py-2 text-right tnum">{i.opening_qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {prepared && (
              <div className="space-y-2">
                {!nameMapped && (
                  <Notice tone="bad" text="Choose which column holds the item name before importing." />
                )}
                {/* The mistake that duplicates a whole catalogue: no code
                    column, so every row looks new. */}
                {nameMapped && !codeMapped && (
                  <Notice
                    tone={mode === "add" ? "warn" : "bad"}
                    text={
                      mode === "update"
                        ? "Without an item code column nothing can be matched to what's already here — every row would be added again. Map the code column, or choose \u201cOnly add what\u2019s new\u201d."
                        : `No item code column, so all ${prepared.items.length} rows will be added as new items even if you already have them.`
                    }
                  />
                )}
                {nameMapped && /\b(code|sku|id)\b/i.test(mapping.name) && (
                  <Notice
                    tone="warn"
                    text={`"${mapping.name}" looks like a code rather than a name — check the preview above before importing.`}
                  />
                )}
                {prepared.newCategories.length > 0 && (
                  <Notice
                    tone="ok"
                    text={`${prepared.newCategories.length} new categor${
                      prepared.newCategories.length === 1 ? "y" : "ies"
                    } will be created: ${prepared.newCategories.slice(0, 4).join(", ")}${
                      prepared.newCategories.length > 4 ? "…" : ""
                    }`}
                  />
                )}
                {prepared.missingCategory > 0 && (
                  <Notice
                    tone="warn"
                    text={`${prepared.missingCategory} row(s) have no category and will import as Uncategorised.`}
                  />
                )}
                {prepared.missingCost > 0 && (
                  <Notice
                    tone="warn"
                    text={`${prepared.missingCost} row(s) have no unit cost, so they won't count towards stock value.`}
                  />
                )}
                {prepared.skipped > 0 && (
                  <Notice
                    tone="warn"
                    text={`${prepared.skipped} row(s) have no item name and will be skipped.`}
                  />
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>What should happen to items already here?</Label>
              {[
                {
                  key: "add",
                  title: "Only add what's new",
                  body: "Anything with a code that already exists is left alone.",
                },
                {
                  key: "update",
                  title: "Update existing and add new",
                  body: "For a file you exported from here, edited, and are putting back.",
                },
                {
                  key: "replace",
                  title: "Replace everything",
                  body: "Deletes all current items, purchases, sales and gift boxes first.",
                },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setMode(opt.key)}
                  className={cn(
                    "w-full text-left rounded-xl border px-3.5 py-3 transition-colors",
                    mode === opt.key
                      ? opt.key === "replace"
                        ? "border-rose-300 bg-rose-50"
                        : "border-brand bg-brand-light"
                      : "border-line hover:bg-brand-light/40"
                  )}
                >
                  <div
                    className={cn(
                      "text-[13px] font-medium",
                      mode === opt.key && opt.key === "replace"
                        ? "text-stock-out"
                        : mode === opt.key
                        ? "text-brand"
                        : "text-ink"
                    )}
                  >
                    {opt.title}
                  </div>
                  <div className="text-[12px] text-muted2 mt-0.5">{opt.body}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={runImport}
            disabled={busy || !prepared?.items.length || !nameMapped || (mode === "update" && !codeMapped)}
            className="bg-brand hover:bg-brand-hover"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              `Import ${prepared?.items.length || 0} items`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Notice({ tone, text }) {
  const tones = {
    ok: "bg-emerald-50 border-emerald-200 text-stock-in",
    warn: "bg-gold-light border-amber-200 text-gold",
    bad: "bg-rose-50 border-rose-200 text-stock-out",
  };
  const Icon = tone === "ok" ? CheckCircle2 : AlertTriangle;
  return (
    <div className={cn("flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5", tones[tone])}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <span className="text-[12.5px] leading-relaxed">{text}</span>
    </div>
  );
}
